import { Loading } from "../../app/Status";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import {
  SALES_BASE_PRICES,
  SALES_MODULES,
  sellerMissing,
  type SalesPriceKey,
  type SalesSeller,
  type SalesSettings,
} from "@klantpunt/shared";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { PlatformGuard, parseEuro } from "../../pages/Platform";
import { PlatformHeader } from "./PlatformHeader";
import { AgreementList, BillingRound, InvoiceList } from "./SalesInvoicing";

const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");
const settingsKey = ["platform", "sales", "settings"] as const;
const euroInput = (c: number) => (c / 100).toFixed(2).replace(".", ",");

export function SalesBillingPage() {
  return (
    <PlatformGuard>
      <SalesBilling />
    </PlatformGuard>
  );
}

const TABS = [
  ["maandronde", "Maandronde"],
  ["facturen", "Facturen"],
  ["overeenkomsten", "Overeenkomsten"],
  ["prijslijst", "Prijslijst"],
  ["verkoper", "Verkoper"],
] as const;
type Tab = (typeof TABS)[number][0];

/**
 * Platformbeheer › Facturatie (docs/VERKOOP.md): maandronde, facturen en overeenkomsten (stap 4),
 * prijslijst en verkopergegevens (stap 1).
 */
function SalesBilling() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.find(([k]) => k === params.get("tab"))?.[0] ?? "maandronde";
  const q = useQuery({ queryKey: settingsKey, queryFn: () => api<SalesSettings>("/platform/sales/settings") });
  return (
    <div className="page platform sales-page">
      <PlatformHeader title="Facturatie" />
      <div className="tabs pf-subtabs" role="tablist" aria-label="Facturatie">
        {TABS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            onClick={() => setParams(k === "maandronde" ? {} : { tab: k }, { replace: true })}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "maandronde" ? (
        <BillingRound />
      ) : tab === "facturen" ? (
        <InvoiceList />
      ) : tab === "overeenkomsten" ? (
        <AgreementList />
      ) : q.isPending || q.isError ? (
        q.isError ? (
          <p className="muted">{errMsg(q.error)}</p>
        ) : (
          <Loading />
        )
      ) : tab === "prijslijst" ? (
        <PriceList settings={q.data} />
      ) : (
        <SellerForm settings={q.data} />
      )}
    </div>
  );
}

function useSaveSettings(msg: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (s: SalesSettings) => api<SalesSettings>("/platform/sales/settings", { method: "PUT", body: s }),
    onSuccess: (s) => {
      qc.setQueryData(settingsKey, s);
      toast(msg);
    },
    onError: (e) => toast(errMsg(e)),
  });
}

function PriceList({ settings }: { settings: SalesSettings }) {
  const [vals, setVals] = useState<Record<SalesPriceKey, string>>(
    () =>
      Object.fromEntries(Object.entries(settings.prices).map(([k, v]) => [k, euroInput(v)])) as Record<
        SalesPriceKey,
        string
      >,
  );
  useEffect(() => {
    setVals(
      Object.fromEntries(Object.entries(settings.prices).map(([k, v]) => [k, euroInput(v)])) as Record<
        SalesPriceKey,
        string
      >,
    );
  }, [settings.prices]);
  const save = useSaveSettings("Prijslijst opgeslagen");
  const parsed = Object.fromEntries(Object.entries(vals).map(([k, v]) => [k, parseEuro(v)])) as Record<
    SalesPriceKey,
    number | null
  >;
  const invalid = Object.values(parsed).some((v) => v === null);
  const dirty = Object.entries(parsed).some(([k, v]) => v !== settings.prices[k as SalesPriceKey]);
  const losse = (parsed.building ?? 0) + SALES_MODULES.reduce((s, m) => s + (parsed[m.key] ?? 0), 0);

  const row = (key: SalesPriceKey, label: string, unit: string, description: string) => (
    <tr key={key}>
      <td>
        <b>{label}</b>
        <span className="small muted co-block">{description}</span>
      </td>
      <td className="dir-hide-narrow">{unit}</td>
      <td className="num">
        <label className="sr-only" htmlFor={`price-${key}`}>
          Prijs {label}
        </label>
        <input
          id={`price-${key}`}
          inputMode="decimal"
          value={vals[key]}
          aria-invalid={parsed[key] === null || undefined}
          onChange={(e) => setVals((v) => ({ ...v, [key]: e.target.value }))}
        />
      </td>
    </tr>
  );

  return (
    <>
      <p className="muted small">
        Bedragen per maand excl. btw, behalve inrichting (eenmalig). Een nieuwe prijs geldt voor nieuwe offertes; wat al
        getekend is, houdt zijn prijs.
      </p>
      <section className="panel tscroll">
        <table className="it price-table" aria-label="Prijslijst">
          <thead>
            <tr>
              <th scope="col">Onderdeel</th>
              <th scope="col" className="dir-hide-narrow">
                Eenheid
              </th>
              <th scope="col" className="num">
                Prijs (€)
              </th>
            </tr>
          </thead>
          <tbody>
            {SALES_BASE_PRICES.filter((p) => p.key !== "allin" && p.key !== "setup").map((p) =>
              row(p.key, p.label, p.unit, p.description),
            )}
            {SALES_MODULES.map((m) => row(m.key, m.label, "per gebouw", m.description))}
            {SALES_BASE_PRICES.filter((p) => p.key === "allin").map((p) =>
              row(p.key, p.label, p.unit, `${p.description}; los € ${euroInput(losse)}`),
            )}
            {SALES_BASE_PRICES.filter((p) => p.key === "setup").map((p) => row(p.key, p.label, p.unit, p.description))}
          </tbody>
        </table>
      </section>
      <p className="btnrow pf-sub">
        <button
          type="button"
          className="btn primary"
          disabled={invalid || !dirty || save.isPending}
          onClick={() => save.mutate({ ...settings, prices: parsed as Record<SalesPriceKey, number> })}
        >
          {save.isPending ? "Bezig met opslaan…" : "Prijslijst opslaan"}
        </button>
        {invalid && <span className="hint err">Controleer de bedragen.</span>}
      </p>
    </>
  );
}

const SELLER_FIELDS: { key: keyof SalesSeller; label: string; hint?: string; type?: string }[] = [
  { key: "tradeName", label: "Handelsnaam", hint: "Zoals op offerte en factuur" },
  { key: "legalName", label: "Bedrijfsnaam (statutair)", hint: "bijv. Klantpunt B.V." },
  { key: "kvk", label: "KvK-nummer" },
  { key: "vat", label: "Btw-nummer" },
  { key: "address", label: "Adres" },
  { key: "postcode", label: "Postcode" },
  { key: "city", label: "Plaats" },
  { key: "iban", label: "IBAN", hint: "Waar klanten naar overmaken" },
  { key: "email", label: "E-mail", type: "email" },
  { key: "phone", label: "Telefoon", type: "tel" },
  { key: "website", label: "Website" },
];

function SellerForm({ settings }: { settings: SalesSettings }) {
  const [f, setF] = useState<SalesSeller>(settings.seller);
  useEffect(() => setF(settings.seller), [settings.seller]);
  const save = useSaveSettings("Verkopergegevens opgeslagen");
  const missing = sellerMissing(f);
  return (
    <>
      {missing.length > 0 && (
        <p className="alert info">
          Nog nodig voor een echte offerte: {missing.join(", ")}. Zolang dit ontbreekt, kun je offertes wel maken maar
          niet versturen.
        </p>
      )}
      <section className="panel">
        <div className="formgrid">
          {SELLER_FIELDS.map((x) => (
            <label key={x.key} className="field">
              <span>{x.label}</span>
              <input
                type={x.type ?? "text"}
                value={f[x.key]}
                onChange={(e) => setF((v) => ({ ...v, [x.key]: e.target.value }))}
              />
              {x.hint && <span className="hint">{x.hint}</span>}
            </label>
          ))}
        </div>
      </section>
      <p className="btnrow pf-sub">
        <button
          type="button"
          className="btn primary"
          disabled={save.isPending}
          onClick={() => save.mutate({ ...settings, seller: f })}
        >
          {save.isPending ? "Bezig met opslaan…" : "Opslaan"}
        </button>
      </p>
    </>
  );
}
